import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../services/api-client';
import {
  authService,
  type LoginRequest,
  type OrganizationOption,
  type SessionResponse,
} from '../services/auth-service';
import { organizationService } from '../services/organization-service';
import { userService } from '../services/user-service';
import type { User } from '../types/schema';
import { queryClient } from '../lib/react-query';
import mixpanel from '../lib/mixpanel';
import { removeLocalStorageCached } from '../lib/storage-cache';

interface AuthContextType {
  user: User | null;
  session: SessionResponse | null;
  organizations: OrganizationOption[];
  loading: boolean;
  switchingOrganizationId: string | null;
  isAuthenticated: boolean;
  login: (request: LoginRequest) => Promise<void>;
  signOut: () => Promise<void>;
  refetchUser: () => Promise<void>;
  selectOrganization: (organizationId: string) => Promise<void>;
  updateOrganizationName: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [switchingOrganizationId, setSwitchingOrganizationId] = useState<string | null>(null);

  const loadAuthenticatedUser = useCallback(async () => {
    const [currentSession, currentUser] = await Promise.all([
      authService.session(),
      userService.getCurrentUser(),
    ]);
    setSession(currentSession);
    setUser(currentUser);
    try {
      setOrganizations(await authService.organizations());
    } catch (error) {
      console.warn('Could not load organization choices', error);
      setOrganizations([
        {
          ...currentSession.organization,
          role: currentSession.membership.role,
          isCurrent: true,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void loadAuthenticatedUser()
      .catch((error) => {
        if (!(error instanceof ApiError && error.statusCode === 401)) {
          console.error('Failed to restore session', error);
        }
        if (active) {
          setSession(null);
          setUser(null);
          setOrganizations([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadAuthenticatedUser]);

  useEffect(() => {
    const clearSession = () => {
      setSession(null);
      setUser(null);
      setOrganizations([]);
      queryClient.clear();
    };
    window.addEventListener('omni:unauthorized', clearSession);
    return () => window.removeEventListener('omni:unauthorized', clearSession);
  }, []);

  useEffect(() => {
    if (!user) return;
    mixpanel.identify(user.id);
    mixpanel.people.set({ $name: user.fullName, $email: user.email });
  }, [user]);

  const login = async (request: LoginRequest) => {
    setLoading(true);
    try {
      await authService.login(request);
      await loadAuthenticatedUser();
      await queryClient.invalidateQueries();
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await authService.logout();
    } finally {
      setSession(null);
      setUser(null);
      setOrganizations([]);
      queryClient.clear();
      mixpanel.reset();
    }
  };

  const refetchUser = async () => {
    const currentUser = await userService.getCurrentUser();
    setUser(currentUser);
  };

  const selectOrganization = async (organizationId: string) => {
    if (!session || organizationId === session.organization.id) return;

    setSwitchingOrganizationId(organizationId);
    try {
      await authService.selectOrganization(organizationId);
      removeLocalStorageCached('omni_current_workspace_id');
      removeLocalStorageCached('workspace_order');
      queryClient.clear();
      await loadAuthenticatedUser();
    } finally {
      setSwitchingOrganizationId(null);
    }
  };

  const updateOrganizationName = async (name: string) => {
    if (!session) return;
    const updated = await organizationService.updateCurrent(name);
    setSession((current) =>
      current
        ? {
            ...current,
            organization: {
              ...current.organization,
              name: updated.name,
            },
          }
        : current,
    );
    setOrganizations((current) =>
      current.map((organization) =>
        organization.id === updated.id
          ? { ...organization, name: updated.name }
          : organization,
      ),
    );
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        organizations,
        loading,
        switchingOrganizationId,
        isAuthenticated: Boolean(session && user),
        login,
        signOut,
        refetchUser,
        selectOrganization,
        updateOrganizationName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}

export function useCurrentUser() {
  const { user, loading } = useAuthContext();
  return { user, loading, error: null as string | null };
}
